import { useEffect, useState } from 'react';

import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

import {
  Table,
  TableBody,
  TableCell,
  TableEmptyState,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../ui/Table';

interface UserVotingStat {
  userEmail: string;
  numVotes: number;
  numPrompts: number;
  firstVote: string;
  lastVote: string;
}

interface DailyVoteCount {
  date: string;
  voteCount: number;
}

interface RecentVote {
  id: string;
  userEmail: string;
  promptText: string;
  choice: string;
  createdAt: string;
}

type SortField =
  | 'num_votes'
  | 'num_prompts'
  | 'first_vote'
  | 'last_vote'
  | 'user_email';
type SortOrder = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  order: SortOrder;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatShortDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatShortDate(dateString);
}

function SortIcon({ active, order }: { active: boolean; order: SortOrder }) {
  return (
    <span
      className={`ml-1 inline-block ${active ? 'text-accent' : 'text-text-light'}`}
    >
      {order === 'asc' ? '▲' : '▼'}
    </span>
  );
}

interface SortableHeaderProps {
  label: string;
  field: SortField;
  currentSort: SortConfig;
  onSort: (field: SortField) => void;
  align?: 'left' | 'center' | 'right';
}

const ALIGN_TO_JUSTIFY: Record<string, string> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

function SortableHeader({
  label,
  field,
  currentSort,
  onSort,
  align = 'left',
}: SortableHeaderProps) {
  const isActive = currentSort.field === field;

  return (
    <TableHeaderCell align={align}>
      <button
        onClick={() => onSort(field)}
        className="flex items-center gap-1 hover:text-accent transition-colors"
        style={{ justifyContent: ALIGN_TO_JUSTIFY[align] }}
      >
        {label}
        <SortIcon
          active={isActive}
          order={isActive ? currentSort.order : 'desc'}
        />
      </button>
    </TableHeaderCell>
  );
}

function StatCard({
  label,
  value,
  subtext,
}: {
  label: string;
  value: string | number;
  subtext?: string;
}) {
  return (
    <div className="bg-white border border-[#D1C9C4] rounded-lg p-4">
      <div className="text-sm text-text-light mb-1">{label}</div>
      <div className="text-2xl font-semibold text-text">{value}</div>
      {subtext && <div className="text-xs text-text-light mt-1">{subtext}</div>}
    </div>
  );
}

function VotesOverTimeChart({ data }: { data: DailyVoteCount[] }) {
  if (data.length === 0) {
    return (
      <div className="text-center text-text-light py-8">No data available</div>
    );
  }

  const maxCount = Math.max(...data.map(d => d.voteCount));

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-1 h-32">
        {data.map(day => {
          const height = maxCount > 0 ? (day.voteCount / maxCount) * 100 : 0;
          return (
            <div
              key={day.date}
              className="flex-1 h-full flex flex-col items-center justify-end group relative"
            >
              <div
                className="w-full bg-accent/80 hover:bg-accent transition-colors rounded-t cursor-pointer"
                style={{ height: `${Math.max(height, 2)}%`, minHeight: '2px' }}
                title={`${formatShortDate(day.date)}: ${day.voteCount} votes`}
              />
              {/* Show tooltip on hover */}
              <div className="absolute bottom-full mb-1 hidden group-hover:block bg-text text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                {formatShortDate(day.date)}: {day.voteCount}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-xs text-text-light">
        <span>{formatShortDate(data[0].date)}</span>
        <span>{formatShortDate(data[data.length - 1].date)}</span>
      </div>
    </div>
  );
}

const CHOICE_LABELS: Record<string, string> = {
  model_a: 'A',
  model_b: 'B',
  a: 'A',
  b: 'B',
  tie: 'Tie',
};

function RecentActivityFeed({ votes }: { votes: RecentVote[] }) {
  if (votes.length === 0) {
    return (
      <div className="text-center text-text-light py-4">No recent activity</div>
    );
  }

  return (
    <div className="space-y-3 max-h-64 overflow-y-auto">
      {votes.map(vote => {
        const isTie = vote.choice === 'tie';
        return (
          <div
            key={vote.id}
            className="flex items-start gap-3 text-sm border-b border-[#E8E3DD] pb-3 last:border-0"
          >
            <div className="flex-1 min-w-0">
              <div className="font-medium text-text truncate">
                {vote.userEmail}
              </div>
              <div className="text-text-light truncate" title={vote.promptText}>
                {vote.promptText.slice(0, 60)}
                {vote.promptText.length > 60 ? '...' : ''}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-xs text-text-light">
                {formatRelativeTime(vote.createdAt)}
              </div>
              <div
                className="text-xs font-medium"
                style={{ color: isTie ? '#8B8580' : '#8B2332' }}
              >
                {CHOICE_LABELS[vote.choice] || vote.choice}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Standalone Votes Over Time component - fetches its own data
 */
export function VotesOverTime() {
  const [data, setData] = useState<DailyVoteCount[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const response = await apiRequest('/api/admin/votes-over-time?days=30');
        setData((response as { data: DailyVoteCount[] }).data);
      } catch (e) {
        logError(
          'Failed to load votes over time',
          e instanceof Error ? e : undefined
        );
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  return (
    <div className="bg-white border border-[#D1C9C4] rounded-lg p-4">
      <h3 className="text-lg font-semibold text-text mb-4">
        Votes Over Time (30 days)
      </h3>
      {loading ? (
        <div className="text-center text-text-light py-8">Loading...</div>
      ) : (
        <VotesOverTimeChart data={data} />
      )}
    </div>
  );
}

/**
 * Standalone Recent Activity component - fetches its own data
 */
export function RecentActivity() {
  const [votes, setVotes] = useState<RecentVote[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const response = await apiRequest(
          '/api/admin/recent-voting-activity?limit=10'
        );
        setVotes((response as { votes: RecentVote[] }).votes);
      } catch (e) {
        logError(
          'Failed to load recent activity',
          e instanceof Error ? e : undefined
        );
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  return (
    <div className="bg-white border border-[#D1C9C4] rounded-lg p-4">
      <h3 className="text-lg font-semibold text-text mb-4">Recent Activity</h3>
      {loading ? (
        <div className="text-center text-text-light py-8">Loading...</div>
      ) : (
        <RecentActivityFeed votes={votes} />
      )}
    </div>
  );
}

export function UserVotingStats() {
  const [stats, setStats] = useState<UserVotingStat[]>([]);
  const [votesOverTime, setVotesOverTime] = useState<DailyVoteCount[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentVote[]>([]);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [emailSearch, setEmailSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Sorting
  const [sort, setSort] = useState<SortConfig>({
    field: 'num_votes',
    order: 'desc',
  });

  // Debounce email search
  const [debouncedEmailSearch, setDebouncedEmailSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmailSearch(emailSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [emailSearch]);

  useEffect(() => {
    async function loadStats() {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams();
        if (debouncedEmailSearch)
          params.set('emailSearch', debouncedEmailSearch);
        if (startDate) params.set('startDate', startDate);
        if (endDate) params.set('endDate', endDate);
        params.set('sortBy', sort.field);
        params.set('sortOrder', sort.order);

        const queryString = params.toString();
        const url = `/api/admin/user-voting-stats${queryString ? `?${queryString}` : ''}`;

        const data: { stats: UserVotingStat[] } = await apiRequest(url);
        setStats(data.stats);
      } catch (e: unknown) {
        const errorMessage =
          e instanceof Error ? e.message : 'Failed to load statistics';
        logError(
          'Failed to load user voting stats',
          e instanceof Error ? e : undefined
        );
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    }

    loadStats();
  }, [debouncedEmailSearch, startDate, endDate, sort]);

  // Load analytics data (votes over time and recent activity)
  useEffect(() => {
    async function loadAnalytics() {
      try {
        setAnalyticsLoading(true);

        const [votesData, activityData] = await Promise.all([
          apiRequest('/api/admin/votes-over-time?days=30'),
          apiRequest('/api/admin/recent-voting-activity?limit=10'),
        ]);

        setVotesOverTime((votesData as { data: DailyVoteCount[] }).data);
        setRecentActivity((activityData as { votes: RecentVote[] }).votes);
      } catch (e: unknown) {
        logError(
          'Failed to load analytics',
          e instanceof Error ? e : undefined
        );
      } finally {
        setAnalyticsLoading(false);
      }
    }

    loadAnalytics();
  }, []);

  // Calculate top 10 users' percentage of total votes
  const totalVotes = stats.reduce((sum, s) => sum + s.numVotes, 0);
  const top10Votes = stats.slice(0, 10).reduce((sum, s) => sum + s.numVotes, 0);
  const top10Percentage =
    totalVotes > 0 ? ((top10Votes / totalVotes) * 100).toFixed(1) : '0';

  function handleSort(field: SortField) {
    setSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'desc' ? 'asc' : 'desc',
    }));
  }

  function clearFilters() {
    setEmailSearch('');
    setStartDate('');
    setEndDate('');
  }

  const hasFilters = emailSearch || startDate || endDate;

  return (
    <div className="space-y-6">
      {/* Analytics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Votes"
          value={totalVotes.toLocaleString()}
          subtext={`from ${stats.length} users`}
        />
        <StatCard
          label="Total Users"
          value={stats.length}
          subtext="who have voted"
        />
        <StatCard
          label="Top 10 Contribution"
          value={`${top10Percentage}%`}
          subtext={`${top10Votes.toLocaleString()} of ${totalVotes.toLocaleString()} votes`}
        />
        <StatCard
          label="Avg Votes/User"
          value={stats.length > 0 ? Math.round(totalVotes / stats.length) : 0}
          subtext="votes per user"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Votes Over Time */}
        <div className="bg-white border border-[#D1C9C4] rounded-lg p-4">
          <h3 className="text-lg font-semibold text-text mb-4">
            Votes Over Time (30 days)
          </h3>
          {analyticsLoading ? (
            <div className="text-center text-text-light py-8">Loading...</div>
          ) : (
            <VotesOverTimeChart data={votesOverTime} />
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white border border-[#D1C9C4] rounded-lg p-4">
          <h3 className="text-lg font-semibold text-text mb-4">
            Recent Activity
          </h3>
          {analyticsLoading ? (
            <div className="text-center text-text-light py-8">Loading...</div>
          ) : (
            <RecentActivityFeed votes={recentActivity} />
          )}
        </div>
      </div>

      {/* User Table */}
      <div className="p-6 bg-white border border-[#D1C9C4] rounded-lg">
        <h2 className="text-xl font-semibold text-text mb-4">
          User Voting Activity
        </h2>

        {/* Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-wrap gap-4">
            {/* Email Search */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm text-text-light mb-1">
                Search by Email
              </label>
              <input
                type="text"
                value={emailSearch}
                onChange={e => setEmailSearch(e.target.value)}
                placeholder="Filter by email..."
                className="w-full px-3 py-2 border border-[#D1C9C4] rounded text-sm focus:outline-none focus:border-accent"
              />
            </div>

            {/* Date Range */}
            <div className="min-w-[160px]">
              <label className="block text-sm text-text-light mb-1">
                From Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-[#D1C9C4] rounded text-sm focus:outline-none focus:border-accent"
              />
            </div>

            <div className="min-w-[160px]">
              <label className="block text-sm text-text-light mb-1">
                To Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-[#D1C9C4] rounded text-sm focus:outline-none focus:border-accent"
              />
            </div>

            {/* Clear Filters */}
            {hasFilters && (
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="px-3 py-2 text-sm text-text-light hover:text-accent transition-colors"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="text-center text-text-light py-8">Loading...</div>
        ) : (
          <>
            {/* Summary */}
            <div className="mb-4 text-sm text-text-light">
              {stats.length} user{stats.length !== 1 ? 's' : ''} found
            </div>

            {/* Table */}
            <Table>
              <TableHead>
                <TableRow hover={false}>
                  <SortableHeader
                    label="Email"
                    field="user_email"
                    currentSort={sort}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Votes"
                    field="num_votes"
                    currentSort={sort}
                    onSort={handleSort}
                    align="right"
                  />
                  <SortableHeader
                    label="Prompts"
                    field="num_prompts"
                    currentSort={sort}
                    onSort={handleSort}
                    align="right"
                  />
                  <SortableHeader
                    label="First Vote"
                    field="first_vote"
                    currentSort={sort}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Last Vote"
                    field="last_vote"
                    currentSort={sort}
                    onSort={handleSort}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.length === 0 ? (
                  <TableEmptyState
                    message={
                      hasFilters
                        ? 'No users match the current filters'
                        : 'No voting data available'
                    }
                    colSpan={5}
                  />
                ) : (
                  stats.map(stat => (
                    <TableRow key={stat.userEmail}>
                      <TableCell>
                        <span className="font-medium text-text">
                          {stat.userEmail}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span className="font-semibold text-accent">
                          {stat.numVotes.toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span className="text-text">
                          {stat.numPrompts.toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-text-light text-sm">
                          {formatDate(stat.firstVote)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-text-light text-sm">
                          {formatDate(stat.lastVote)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </>
        )}
      </div>
    </div>
  );
}
